import { useCallback } from 'react'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import {
  featureFlagKeyTypeFromKey,
  helpFeatureFlagForModule,
} from '@scrambl/shared'

interface HelpEntryLike {
  featureFlagKey?: string | null
  moduleSlug?: string | null
}

interface HelpCategoryLike {
  slug: string
  featureFlagKey?: string | null
}

/**
 * Feature-flag gate for the Help Center. The help library is a platform-level
 * (global) table, so filtering happens client-side against the CURRENT
 * organization's effective flags — the same source the sidebar and route guards
 * use. A personal account (business flags OFF) therefore only ever sees general
 * + enabled-feature help; a business org that disabled a module loses its help
 * too, with no server round-trip.
 *
 * Fail-closed while flags are still loading: feature-gated content stays hidden
 * until the flag map resolves so a personal account never flashes business help.
 * General (null-key) content is always visible. Because useFeatureFlags is used
 * app-wide, the flag map is normally already warm by the time /help renders.
 */
export function useHelpVisibility() {
  const { flagsData } = useFeatureFlags()

  const isFeatureFlagEnabled = useCallback(
    (featureFlagKey: string | null | undefined): boolean => {
      if (!featureFlagKey) return true
      const flagType = featureFlagKeyTypeFromKey(featureFlagKey)
      // Unknown key → fail open so a new/unmapped flag never hides content.
      if (!flagType) return true
      // Flags not yet resolved → fail closed (hide gated content).
      if (!flagsData) return false
      return flagsData[flagType] ?? false
    },
    [flagsData],
  )

  const isEntryVisible = useCallback(
    (entry: HelpEntryLike): boolean =>
      isFeatureFlagEnabled(
        entry.featureFlagKey ?? helpFeatureFlagForModule(entry.moduleSlug),
      ),
    [isFeatureFlagEnabled],
  )

  const isCategoryVisible = useCallback(
    (category: HelpCategoryLike): boolean =>
      isFeatureFlagEnabled(
        category.featureFlagKey ?? helpFeatureFlagForModule(category.slug),
      ),
    [isFeatureFlagEnabled],
  )

  return {
    isFeatureFlagEnabled,
    isEntryVisible,
    isCategoryVisible,
    /** True once the org's flag map has resolved. */
    flagsReady: !!flagsData,
  }
}
